import { defineMutator } from "@rocicorp/zero"

import { validateLanguageCode } from "@productlane/lib/src/translations/validate-language"

import type { MutatorTx } from "../schema"
import type { ReadonlyJSONValue } from "@rocicorp/zero"

export const issueTranslationMutators = {
	upsert: defineMutator(
		async ({
			tx,
			args: { id, ...data },
		}: {
			tx: MutatorTx
			args: {
				id: string
				workspaceId: string
				language: string
				issueId: string
				title?: string | null
				description?: string | null
				createdAt?: number
			}
		}) => {
			validateLanguageCode(data.language)
			await tx.mutate.issueTranslations.upsert({
				id,
				createdAt: Date.now(),
				...data,
				updatedAt: Date.now(),
			})
		},
	),

	delete: defineMutator(
		async ({ tx, args: { id } }: { tx: MutatorTx; args: { id: string } }) => {
			await tx.mutate.issueTranslations.delete({ id })
		},
	),

	deleteByLanguage: defineMutator(
		async ({
			tx,
			args: { workspaceId, language },
		}: {
			tx: MutatorTx
			args: { workspaceId: string; language: string }
		}) => {
			validateLanguageCode(language)
			const translations = await tx.query.issueTranslations
				.where("workspaceId", workspaceId)
				.where("language", language)
				.run()

			await Promise.all(
				translations.map((translation) =>
					tx.mutate.issueTranslations.delete({ id: translation.id }),
				),
			)
		},
	),
}

export const projectTranslationMutators = {
	upsert: defineMutator(
		async ({
			tx,
			args: { id, ...data },
		}: {
			tx: MutatorTx
			args: {
				id: string
				workspaceId: string
				language: string
				projectId: string
				name?: string | null
				description?: string | null
				summary?: string | null
				createdAt?: number
			}
		}) => {
			validateLanguageCode(data.language)
			await tx.mutate.projectTranslations.upsert({
				id,
				createdAt: Date.now(),
				...data,
				updatedAt: Date.now(),
			})
		},
	),

	delete: defineMutator(
		async ({ tx, args: { id } }: { tx: MutatorTx; args: { id: string } }) => {
			await tx.mutate.projectTranslations.delete({ id })
		},
	),

	deleteByLanguage: defineMutator(
		async ({
			tx,
			args: { workspaceId, language },
		}: {
			tx: MutatorTx
			args: { workspaceId: string; language: string }
		}) => {
			validateLanguageCode(language)
			const translations = await tx.query.projectTranslations
				.where("workspaceId", workspaceId)
				.where("language", language)
				.run()

			await Promise.all(
				translations.map((translation) =>
					tx.mutate.projectTranslations.delete({ id: translation.id }),
				),
			)
		},
	),
}

export const changelogTranslationMutators = {
	upsert: defineMutator(
		async ({
			tx,
			args: { id, ...data },
		}: {
			tx: MutatorTx
			args: {
				id: string
				workspaceId: string
				language: string
				changelogId: string
				title: string
				content?: string | null
				summary?: string | null
				imageUrl?: string | null
				coverVideoUrl?: string | null
				notes?: ReadonlyJSONValue | null
				translationStatus?:
					| "QUEUED"
					| "TRANSLATING"
					| "COMPLETED"
					| "FAILED"
					| null
				createdAt?: number
			}
		}) => {
			validateLanguageCode(data.language)
			await tx.mutate.changelogTranslations.upsert({
				id,
				createdAt: Date.now(),
				...data,
				updatedAt: Date.now(),
			})
		},
	),

	delete: defineMutator(
		async ({ tx, args: { id } }: { tx: MutatorTx; args: { id: string } }) => {
			await tx.mutate.changelogTranslations.delete({ id })
		},
	),

	deleteByLanguage: defineMutator(
		async ({
			tx,
			args: { workspaceId, language },
		}: {
			tx: MutatorTx
			args: { workspaceId: string; language: string }
		}) => {
			validateLanguageCode(language)
			const translations = await tx.query.changelogTranslations
				.where("workspaceId", workspaceId)
				.where("language", language)
				.run()

			await Promise.all(
				translations.map((translation) =>
					tx.mutate.changelogTranslations.delete({ id: translation.id }),
				),
			)
		},
	),
}

export const helpCenterArticleTranslationMutators = {
	upsert: defineMutator(
		async ({
			tx,
			args: { id, ...data },
		}: {
			tx: MutatorTx
			args: {
				id: string
				workspaceId: string
				language: string
				helpCenterArticleId: string
				title?: string | null
				content?: string | null
				summary?: string | null
				imageUrl?: string | null
				translationStatus?:
					| "QUEUED"
					| "TRANSLATING"
					| "COMPLETED"
					| "FAILED"
					| null
				createdAt?: number
			}
		}) => {
			validateLanguageCode(data.language)
			await tx.mutate.helpCenterArticleTranslations.upsert({
				id,
				createdAt: Date.now(),
				title: data.title ?? "",
				content: data.content ?? "",
				summary: data.summary ?? "",
				workspaceId: data.workspaceId,
				language: data.language,
				helpCenterArticleId: data.helpCenterArticleId,
				imageUrl: data.imageUrl ?? null,
				updatedAt: Date.now(),
			})
		},
	),

	delete: defineMutator(
		async ({ tx, args: { id } }: { tx: MutatorTx; args: { id: string } }) => {
			await tx.mutate.helpCenterArticleTranslations.delete({ id })
		},
	),

	deleteByLanguage: defineMutator(
		async ({
			tx,
			args: { workspaceId, language },
		}: {
			tx: MutatorTx
			args: { workspaceId: string; language: string }
		}) => {
			validateLanguageCode(language)
			const translations = await tx.query.helpCenterArticleTranslations
				.where("workspaceId", workspaceId)
				.where("language", language)
				.run()

			await Promise.all(
				translations.map((translation) =>
					tx.mutate.helpCenterArticleTranslations.delete({
						id: translation.id,
					}),
				),
			)
		},
	),
}

export const helpCenterGroupTranslationMutators = {
	upsert: defineMutator(
		async ({
			tx,
			args: { id, ...data },
		}: {
			tx: MutatorTx
			args: {
				id: string
				workspaceId: string
				language: string
				helpCenterGroupId: string
				title?: string | null
				description?: string | null
				createdAt?: number
			}
		}) => {
			validateLanguageCode(data.language)
			await tx.mutate.helpCenterGroupTranslations.upsert({
				id,
				createdAt: Date.now(),
				...data,
				updatedAt: Date.now(),
			})
		},
	),

	delete: defineMutator(
		async ({ tx, args: { id } }: { tx: MutatorTx; args: { id: string } }) => {
			await tx.mutate.helpCenterGroupTranslations.delete({ id })
		},
	),

	deleteByLanguage: defineMutator(
		async ({
			tx,
			args: { workspaceId, language },
		}: {
			tx: MutatorTx
			args: { workspaceId: string; language: string }
		}) => {
			validateLanguageCode(language)
			const translations = await tx.query.helpCenterGroupTranslations
				.where("workspaceId", workspaceId)
				.where("language", language)
				.run()

			await Promise.all(
				translations.map((translation) =>
					tx.mutate.helpCenterGroupTranslations.delete({ id: translation.id }),
				),
			)
		},
	),
}

export const helpCenterHomeTranslationMutators = {
	upsert: defineMutator(
		async ({
			tx,
			args: { id, ...data },
		}: {
			tx: MutatorTx
			args: {
				id: string
				workspaceId: string
				language: string
				title?: string | null
				summary?: string | null
				createdAt?: number
			}
		}) => {
			validateLanguageCode(data.language)
			await tx.mutate.helpCenterHomeTranslations.upsert({
				id,
				createdAt: Date.now(),
				...data,
				updatedAt: Date.now(),
			})
		},
	),

	delete: defineMutator(
		async ({ tx, args: { id } }: { tx: MutatorTx; args: { id: string } }) => {
			await tx.mutate.helpCenterHomeTranslations.delete({ id })
		},
	),

	deleteByLanguage: defineMutator(
		async ({
			tx,
			args: { workspaceId, language },
		}: {
			tx: MutatorTx
			args: { workspaceId: string; language: string }
		}) => {
			validateLanguageCode(language)
			const translations = await tx.query.helpCenterHomeTranslations
				.where("workspaceId", workspaceId)
				.where("language", language)
				.run()

			await Promise.all(
				translations.map((translation) =>
					tx.mutate.helpCenterHomeTranslations.delete({ id: translation.id }),
				),
			)
		},
	),
}

export const aiChatSettingsTranslationMutators = {
	upsert: defineMutator(
		async ({
			tx,
			args: { id, ...data },
		}: {
			tx: MutatorTx
			args: {
				id: string
				workspaceId: string
				language: string
				welcomeMessage?: string | null
				exampleQuestions: string[]
				createdAt?: number
			}
		}) => {
			validateLanguageCode(data.language)
			await tx.mutate.aiChatSettingsTranslations.upsert({
				id,
				createdAt: Date.now(),
				version: 1,
				isDeleted: false,
				...data,
				updatedAt: Date.now(),
			})
		},
	),

	delete: defineMutator(
		async ({ tx, args: { id } }: { tx: MutatorTx; args: { id: string } }) => {
			await tx.mutate.aiChatSettingsTranslations.delete({ id })
		},
	),

	deleteByLanguage: defineMutator(
		async ({
			tx,
			args: { workspaceId, language },
		}: {
			tx: MutatorTx
			args: { workspaceId: string; language: string }
		}) => {
			validateLanguageCode(language)
			const translations = await tx.query.aiChatSettingsTranslations
				.where("workspaceId", workspaceId)
				.where("language", language)
				.run()

			await Promise.all(
				translations.map((translation) =>
					tx.mutate.aiChatSettingsTranslations.delete({ id: translation.id }),
				),
			)
		},
	),
}
